import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { ExportType } from '../_common/helpers/exportType'
import { CombineOption } from '../_common/helpers/combineOption'
import { AccountingSystem } from '$constants'
import { exceptionMessageHandler } from '$utils'
import {
  invoiceService,
  purchaseOrderService,
  supplierStatementService,
} from '$services'
import { ReportsSlice } from '../../slice'

interface AccountingSystemExportState {
  exportType: ExportType
  selectedItems: string[]
  accountingSystemOption: AccountingSystem
  combineOption: CombineOption

  // To export
  suppliersIdsToExport: string[]
  supplierStatementsIdsToExport: string[]
  invoicesIdsToExport: string[]
  purchaseOrdersIdsToExport: string[]
}

export interface AccountingSystemExportSlice
  extends AccountingSystemExportState {
  resetAccountingSystemExportSlice: () => void

  setSuppliersIdsToExport: (ids: string[]) => void
  setSupplierStatementsIdsToExport: (ids: string[]) => void
  setInvoicesIdsToExport: (ids: string[]) => void
  setPurchaseOrdersIdsToExport: (ids: string[]) => void

  setExportType: (exportType: ExportType) => void

  setSelectedItems: (selectedItems: string[]) => void

  setAccountingSystemOption: (accountingSystem: AccountingSystem) => void

  setCombineOption: (combineOption: CombineOption) => void

  updateThirdPartyInvoiceId: (
    id: string,
    type: 'Invoice' | 'PurchaseOrder' | 'SupplierStatement',
    thirdPartyInvoiceId: string | undefined
  ) => Promise<void>
}

const initialState: AccountingSystemExportState = {
  exportType: 'orders',
  selectedItems: [],
  accountingSystemOption: AccountingSystem.Xero,
  combineOption: 'combined',
  suppliersIdsToExport: [],
  supplierStatementsIdsToExport: [],
  invoicesIdsToExport: [],
  purchaseOrdersIdsToExport: [],
}

const createAccountingSystemExportSlice: StateCreator<
  AccountingSystemExportSlice & SharedSlice & ReportsSlice,
  [],
  [],
  AccountingSystemExportSlice
> = (set) => ({
  ...initialState,

  resetAccountingSystemExportSlice: () => set(initialState),

  setSuppliersIdsToExport: (ids: string[]) =>
    set(() => ({ suppliersIdsToExport: ids })),
  setSupplierStatementsIdsToExport: (ids: string[]) =>
    set(() => ({ supplierStatementsIdsToExport: ids })),
  setInvoicesIdsToExport: (ids: string[]) =>
    set(() => ({ invoicesIdsToExport: ids })),
  setPurchaseOrdersIdsToExport: (ids: string[]) =>
    set(() => ({ purchaseOrdersIdsToExport: ids })),

  setExportType: (exportType: ExportType) => set({ exportType }),

  setSelectedItems: (selectedItems: string[]) => set({ selectedItems }),

  setAccountingSystemOption: (accountingSystem: AccountingSystem) =>
    set({ accountingSystemOption: accountingSystem }),

  setCombineOption: (combineOption: CombineOption) => set({ combineOption }),

  updateThirdPartyInvoiceId: async (
    id: string,
    type: 'Invoice' | 'PurchaseOrder' | 'SupplierStatement',
    thirdPartyInvoiceId: string | undefined
  ) => {
    try {
      if (type === 'Invoice' || type === 'PurchaseOrder') {
        if (type === 'Invoice') {
          await invoiceService.updateThirdPartyInvoiceId(
            id,
            thirdPartyInvoiceId
          )
        } else if (type === 'PurchaseOrder') {
          await purchaseOrderService.updateThirdPartyInvoiceId(
            id,
            thirdPartyInvoiceId
          )
        }

        set((state) => ({
          stockReceivedItems: state.stockReceivedItems.map((item) =>
            item.id === id
              ? { ...item, thirdPartyInvoiceId: thirdPartyInvoiceId }
              : item
          ),
        }))
      } else if (type === 'SupplierStatement') {
        await supplierStatementService.updateThirdPartyInvoiceId(
          id,
          thirdPartyInvoiceId
        )

        set((state) => ({
          supplierStatements: state.supplierStatements.map((item) =>
            item.id === id
              ? { ...item, xeroInvoiceId: thirdPartyInvoiceId }
              : item
          ),
          stockReceivedItems: state.stockReceivedItems.map((item) => {
            const isStockReceivedFound = state.supplierStatements.find(
              (statement) =>
                statement.reconciledStockReceivedItems?.some(
                  (stockItem) => stockItem.id === item.id
                )
            )
            return isStockReceivedFound
              ? { ...item, thirdPartyInvoiceId: thirdPartyInvoiceId }
              : item
          }),
        }))
      }
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to update third party invoice id')
    }
  },
})

export default createAccountingSystemExportSlice
